/**
 * Lab401 VAT Toggle — Tax detection and price switching
 *
 * Architecture:
 *   - Every Liquid-rendered price element carries a `data-tax-cents`
 *     attribute holding the raw ex-VAT value in Shopify cents.
 *   - tax.js finds ALL `[data-tax-cents]` elements, applies the tax rate,
 *     and updates the displayed text in-place using textContent / safe DOM
 *     methods.  No extra DOM elements are created, so the original HTML
 *     structure, CSS classes, ARIA attributes, and structured data (JSON-LD)
 *     remain untouched.
 *   - A MutationObserver re-processes price elements added to the page after
 *     initial load (predictive search, lazy sections, recently-viewed,
 *     quickshop modals, AJAX cart updates).
 *   - Variant changes on the product page are detected via MutationObserver;
 *     the new price is parsed back to cents using the known money_format so
 *     tax can be re-applied.
 *
 * Security:
 *   - No innerHTML is used.  All DOM writes use textContent or
 *     createElement / appendChild.  Money values are numeric — they never
 *     contain user-supplied strings.
 *
 * Accessibility:
 *   - No structural DOM changes — screen readers see the same elements.
 *   - Product-page price container has aria-live="polite" (set by theme),
 *     so price updates are announced automatically.
 *   - JSON-LD structured data is rendered in a separate Liquid template and
 *     is never touched by this script.
 */
(function () {
  "use strict";

  // --------------------------------------------------------------------------
  // Money formatting (standalone — no dependency on global Shopify object)
  // --------------------------------------------------------------------------
  var ShopifyMoney = {
    formatMoney: function (cents, format) {
      if (typeof cents === "string") cents = cents.replace(".", "");
      var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      var formatString = format || "${{amount}}";

      function defaultOpt(opt, def) {
        return typeof opt === "undefined" ? def : opt;
      }

      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultOpt(precision, 2);
        thousands = defaultOpt(thousands, ",");
        decimal = defaultOpt(decimal, ".");
        if (isNaN(number) || number == null) return "0";
        number = (number / 100.0).toFixed(precision);
        var parts = number.split(".");
        var dollars = parts[0].replace(
          /(\d)(?=(\d\d\d)+(?!\d))/g,
          "$1" + thousands
        );
        var centsPart = parts[1] ? decimal + parts[1] : "";
        return dollars + centsPart;
      }

      var match = formatString.match(placeholderRegex);
      if (!match) return "" + cents;
      var value = "";

      switch (match[1]) {
        case "amount":
          value = formatWithDelimiters(cents, 2);
          break;
        case "amount_no_decimals":
          value = formatWithDelimiters(cents, 0);
          break;
        case "amount_with_comma_separator":
          value = formatWithDelimiters(cents, 2, ".", ",");
          break;
        case "amount_no_decimals_with_comma_separator":
          value = formatWithDelimiters(cents, 0, ".", ",");
          break;
        case "amount_with_space_separator":
          value = formatWithDelimiters(cents, 2, " ", ",");
          break;
        case "amount_no_decimals_with_space_separator":
          value = formatWithDelimiters(cents, 0, " ", "");
          break;
        case "amount_with_apostrophe_separator":
          value = formatWithDelimiters(cents, 2, "'", ".");
          break;
      }
      return formatString.replace(placeholderRegex, value);
    },
  };

  // --------------------------------------------------------------------------
  // Parse a formatted money string back to cents using the known format.
  // Used to detect variant-change price updates from empire.js.
  // --------------------------------------------------------------------------
  function parseMoney(formattedPrice, format) {
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholderRegex);
    if (!match) return NaN;

    var pieces = format.split(placeholderRegex);
    var prefix = pieces[0] || "";
    var suffix = pieces[2] || "";
    var numberStr = formattedPrice;
    if (prefix) numberStr = numberStr.replace(prefix, "");
    if (suffix) {
      var suffixIdx = numberStr.lastIndexOf(suffix);
      if (suffixIdx > -1) numberStr = numberStr.substring(0, suffixIdx);
    }
    numberStr = numberStr.trim();

    switch (match[1]) {
      case "amount":
      case "amount_with_apostrophe_separator":
        numberStr = numberStr.replace(/[^0-9.]/g, "");
        return Math.round(parseFloat(numberStr) * 100);
      case "amount_no_decimals":
        numberStr = numberStr.replace(/[^0-9]/g, "");
        return Math.round(parseFloat(numberStr) * 100);
      case "amount_with_comma_separator":
        numberStr = numberStr.replace(/\./g, "").replace(",", ".");
        return Math.round(parseFloat(numberStr) * 100);
      case "amount_no_decimals_with_comma_separator":
        numberStr = numberStr.replace(/\./g, "");
        return Math.round(parseFloat(numberStr) * 100);
      case "amount_with_space_separator":
        numberStr = numberStr.replace(/\s/g, "").replace(",", ".");
        return Math.round(parseFloat(numberStr) * 100);
      case "amount_no_decimals_with_space_separator":
        numberStr = numberStr.replace(/\s/g, "");
        return Math.round(parseFloat(numberStr) * 100);
    }
    return NaN;
  }

  // --------------------------------------------------------------------------
  // Detect the store money format
  // --------------------------------------------------------------------------
  function detectMoneyFormat() {
    if (window.LAB401_MONEY_FORMAT) return window.LAB401_MONEY_FORMAT;
    if (window.Shopify && window.Shopify.money_format)
      return window.Shopify.money_format;
    if (window.theme && window.theme.moneyFormat)
      return window.theme.moneyFormat;
    return "\u20ac{{amount_with_comma_separator}}";
  }

  // --------------------------------------------------------------------------
  // Detect the decimal separator used by the current money format
  // --------------------------------------------------------------------------
  function getDecimalSeparator(format) {
    if (
      format.indexOf("amount_with_comma_separator") > -1 ||
      format.indexOf("amount_with_space_separator") > -1
    ) {
      return ",";
    }
    return ".";
  }

  // --------------------------------------------------------------------------
  // Main initialisation
  // --------------------------------------------------------------------------
  function init() {
    var moneyFormat = detectMoneyFormat();
    var decSep = getDecimalSeparator(moneyFormat);
    var hasDecimals =
      moneyFormat.indexOf("no_decimals") === -1;
    var taxRate = parseFloat(localStorage.getItem("tax_rate")) || 1.0;
    var taxPref = localStorage.getItem("tax_preference") === "true";
    var isUpdating = false; // guard against re-entrant observer calls

    // ---- Tax preference helpers -------------------------------------------
    function getTaxPref() {
      return localStorage.getItem("tax_preference") === "true";
    }
    function setTaxPref(val) {
      localStorage.setItem("tax_preference", String(val));
      taxPref = val;
    }
    function getTaxRate() {
      return parseFloat(localStorage.getItem("tax_rate")) || 1.0;
    }
    function setTaxRate(rate) {
      localStorage.setItem("tax_rate", String(rate));
      taxRate = rate;
    }

    // Expose for external scripts that may need to interact with the toggle
    window.taxHandler = {
      getTaxPreference: getTaxPref,
      setTaxPreference: setTaxPref,
      getTaxRate: getTaxRate,
      setTaxRate: setTaxRate,
      refresh: processAllPrices,
    };

    // ---- Write a formatted price into an element using safe DOM methods ---
    function setPrice(el, formattedPrice) {
      var isSuperscript = el.classList.contains("element-text--price-superscript");

      if (isSuperscript && hasDecimals) {
        // Build <span>integer</span><sup>decimal</sup> structure safely
        var parts = formattedPrice.split(decSep);
        // Clear existing children
        while (el.firstChild) el.removeChild(el.firstChild);
        if (parts.length === 2) {
          var span = document.createElement("span");
          span.textContent = parts[0];
          var sup = document.createElement("sup");
          sup.textContent = parts[1];
          el.appendChild(span);
          el.appendChild(sup);
        } else {
          el.textContent = formattedPrice;
        }
      } else {
        el.textContent = formattedPrice;
      }
    }

    // ---- Read the current display text of a price element -----------------
    function readPrice(el) {
      return el.textContent.trim();
    }

    // ---- Hidden template selector (empire.js uses these as DOM templates) --
    var HIDDEN_TMPL =
      "[data-current-price-hidden]," +
      "[data-current-price-range-hidden]," +
      "[data-compare-price-hidden]," +
      "[data-compare-price-range-hidden]";

    function isHiddenTemplate(el) {
      return el.closest && el.closest(HIDDEN_TMPL);
    }

    // ---- Process a single [data-tax-cents] element ------------------------
    function processEl(el) {
      // Never modify hidden template elements — empire.js copies their
      // innerHTML when switching variants, so they must keep the original
      // Liquid-rendered ex-VAT values.
      if (isHiddenTemplate(el)) return;

      var cents = parseFloat(el.getAttribute("data-tax-cents"));
      if (isNaN(cents)) return;

      // Store original formatted text the first time we encounter this element
      if (!el.hasAttribute("data-tax-original")) {
        el.setAttribute("data-tax-original", readPrice(el));
        el.setAttribute("data-tax-original-cents", String(cents));
      }

      if (taxPref && taxRate !== 1.0) {
        var taxCents = cents * taxRate;
        setPrice(el, ShopifyMoney.formatMoney(taxCents, moneyFormat));
      } else {
        // Restore original ex-VAT price.  Reconstruct from the original cents
        // so the formatting exactly matches what ShopifyMoney produces, even
        // after variant changes.
        var origCents = parseFloat(el.getAttribute("data-tax-original-cents"));
        if (!isNaN(origCents)) {
          setPrice(el, ShopifyMoney.formatMoney(origCents, moneyFormat));
        } else {
          el.textContent = el.getAttribute("data-tax-original");
        }
      }
    }

    // ---- Process ALL [data-tax-cents] elements ----------------------------
    function processAllPrices() {
      isUpdating = true;
      taxPref = getTaxPref();
      taxRate = getTaxRate();
      document.querySelectorAll("[data-tax-cents]").forEach(processEl);
      isUpdating = false;
    }

    // ---- Detect variant-change price updates from empire.js ---------------
    // Empire.js updates prices in two steps when a variant is selected:
    //   1. Copies innerHTML from a hidden template into the visible container
    //   2. Calls .html(Shopify.formatMoney(variant.price)) on the [data-price] span
    // We debounce to let both steps complete, then parse the final displayed
    // price as the new base (ex-VAT) value.
    function detectVariantPriceChanges() {
      var selectors =
        "[data-product-pricing] [data-price]," +
        "[data-product-pricing] [data-price-compare]," +
        "[data-product-wrapper] [data-price]," +
        "[data-product-wrapper] [data-price-compare]";
      var priceEls = document.querySelectorAll(selectors);
      if (!priceEls.length) return;

      isUpdating = true;
      priceEls.forEach(function (el) {
        if (isHiddenTemplate(el)) return;
        var currentText = readPrice(el);
        if (!currentText) return;

        // Parse the displayed price back to cents
        var displayedCents = parseMoney(currentText, moneyFormat);
        if (isNaN(displayedCents)) return;

        var knownCents = parseFloat(el.getAttribute("data-tax-original-cents"));

        // Check if the displayed text matches what we expect (either the
        // ex-VAT original or the tax-applied version).  If it matches
        // either, empire.js hasn't changed it — nothing to do.
        if (!isNaN(knownCents)) {
          var normalise = function (s) { return s.replace(/\s+/g, ""); };
          var expectedExVat = ShopifyMoney.formatMoney(knownCents, moneyFormat);
          var expectedWithTax = ShopifyMoney.formatMoney(knownCents * taxRate, moneyFormat);
          if (normalise(currentText) === normalise(expectedExVat) ||
              normalise(currentText) === normalise(expectedWithTax)) {
            return; // price hasn't changed, skip
          }
        }

        // Empire.js wrote a new variant price — adopt it as the new base
        el.setAttribute("data-tax-cents", String(displayedCents));
        el.setAttribute("data-tax-original", currentText);
        el.setAttribute("data-tax-original-cents", String(displayedCents));
        processEl(el);
      });
      isUpdating = false;
    }

    // ---- MutationObserver: detect dynamically added price elements --------
    // Empire.js replaces innerHTML of [data-price-container] and
    // [data-price-compare-container] when variants change.  We must not
    // process those added nodes immediately (they carry stale data-tax-cents
    // from the hidden template).  Instead we debounce and let empire.js
    // finish its .html() update, then detectVariantPriceChanges runs.
    var PRICE_CONTAINERS =
      "[data-price-container],[data-price-compare-container]";
    var variantDebounceTimer = null;

    var observer = new MutationObserver(function (mutations) {
      if (isUpdating) return;
      var hasPriceContainerMutation = false;

      for (var i = 0; i < mutations.length; i++) {
        var mut = mutations[i];
        var mutTarget = mut.target.nodeType === 1 ? mut.target : mut.target.parentElement;

        // Detect mutations directly on a price container (empire.js innerHTML swap)
        if (mutTarget && mutTarget.matches && mutTarget.matches(PRICE_CONTAINERS)) {
          hasPriceContainerMutation = true;
          continue; // Don't process added nodes from this mutation
        }

        // Also detect text changes inside a [data-price] (empire.js .html() call)
        if (mutTarget && mutTarget.closest &&
            mutTarget.closest("[data-price],[data-price-compare]") &&
            !isHiddenTemplate(mutTarget)) {
          hasPriceContainerMutation = true;
          continue;
        }

        var added = mut.addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.closest && node.closest(HIDDEN_TMPL)) continue;

          if (node.hasAttribute && node.hasAttribute("data-tax-cents")) {
            processEl(node);
          }
          if (node.querySelectorAll) {
            var els = node.querySelectorAll("[data-tax-cents]");
            if (els.length) {
              isUpdating = true;
              els.forEach(processEl);
              isUpdating = false;
            }
          }
        }
      }

      // Debounce price container mutations to let empire.js finish
      if (hasPriceContainerMutation) {
        if (variantDebounceTimer) clearTimeout(variantDebounceTimer);
        variantDebounceTimer = setTimeout(function () {
          variantDebounceTimer = null;
          detectVariantPriceChanges();
        }, 100);
      }
    });

    // ---- Bind the VAT toggle checkboxes -----------------------------------
    // Header + mobile toggles share data-tax-preference (ids must stay unique)
    var allCheckboxes = document.querySelectorAll("[data-tax-preference]");

    function syncCheckboxes(checked) {
      allCheckboxes.forEach(function (cb) {
        cb.checked = checked;
      });
    }

    syncCheckboxes(taxPref);
    processAllPrices();

    allCheckboxes.forEach(function (cb) {
      cb.addEventListener("change", function () {
        setTaxPref(cb.checked);
        syncCheckboxes(cb.checked);
        processAllPrices();
      });
    });

    // Start observing
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    // ---- Poll for late-arriving content (recently viewed, recommendations)
    var pollCount = 0;
    var poller = setInterval(function () {
      pollCount++;
      if (pollCount > 20) {
        clearInterval(poller);
        return;
      }
      var lateEls = document.querySelectorAll(
        "[data-recently-viewed-container] [data-tax-cents]:not([data-tax-original])," +
        "[data-product-recommendations] [data-tax-cents]:not([data-tax-original])"
      );
      if (lateEls.length > 0) {
        clearInterval(poller);
        isUpdating = true;
        lateEls.forEach(processEl);
        isUpdating = false;
      }
    }, 1000);

    // ---- EU VAT rate table -------------------------------------------------
    var EU_VAT = {
      AT: 1.20,  BE: 1.21,  BG: 1.20,  HR: 1.25,  CY: 1.19,
      CZ: 1.21,  DK: 1.25,  EE: 1.22,  FI: 1.255, FR: 1.20,
      DE: 1.19,  EL: 1.24,  HU: 1.27,  IE: 1.23,  IT: 1.22,
      LV: 1.21,  LT: 1.21,  LU: 1.17,  MT: 1.18,  NL: 1.21,
      PL: 1.23,  PT: 1.23,  RO: 1.19,  SK: 1.23,  SI: 1.22,
      ES: 1.21,  SE: 1.25,
    };
    // Greece: GeoIP returns "GR", EU uses "EL"
    EU_VAT.GR = EU_VAT.EL;

    // ---- Read customer data from Liquid snippet ----------------------------
    var customerData = null;
    var customerDataEl = document.getElementById("tax-customer-data");
    if (customerDataEl) {
      try {
        customerData = JSON.parse(customerDataEl.textContent);
      } catch (e) {
        console.log("Tax.js: failed to parse customer data");
      }
    }

    // ---- Show/hide VAT toggle for non-EU countries -------------------------
    function setToggleVisibility(visible) {
      var toggles = document.querySelectorAll(".vatToggle");
      for (var i = 0; i < toggles.length; i++) {
        toggles[i].style.display = visible ? "flex" : "none";
      }
    }

    // ---- Determine country: customer address > GeoIP ----------------------
    function applyCountry(country, source) {
      console.log("Tax.js: using country " + country + " (source: " + source + ")");

      var shippingEl = document.getElementById("shipping-country");
      if (shippingEl) {
        shippingEl.value = country.toLowerCase();
        shippingEl.dispatchEvent(new Event("change"));
      }

      if (country in EU_VAT) {
        setTaxRate(EU_VAT[country]);
        setToggleVisibility(true);
        if (localStorage.getItem("tax_preference") === null) {
          setTaxPref(true);
          syncCheckboxes(true);
        }
      } else {
        setTaxRate(1.0);
        setToggleVisibility(false);
        if (localStorage.getItem("tax_preference") === null) {
          setTaxPref(false);
          syncCheckboxes(false);
        }
      }

      // VAT-exempt customers: disable tax by default
      if (customerData && customerData.vatExempt) {
        console.log("Tax.js: customer is VAT-exempt (reverse charge)");
        if (localStorage.getItem("tax_preference") === null) {
          setTaxPref(false);
          syncCheckboxes(false);
        }
      }

      processAllPrices();
    }

    // If logged-in customer has a shipping address, use that country
    if (customerData && customerData.country) {
      var custCountry = customerData.country.toUpperCase();
      applyCountry(custCountry, "customer address");
    } else {
      // Fall back to GeoIP
      fetch("https://geoip.lab401.workers.dev/index.json")
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var country = (data.country || "").toUpperCase();
          applyCountry(country, "geoip");
        })
        .catch(function () {
          console.log("Tax.js: GeoIP failed, defaulting to non-VAT region");
          applyCountry("US", "fallback");
        });
    }

    // ---- Back/forward cache restore ---------------------------------------
    window.addEventListener("pageshow", function (e) {
      if (e.persisted) processAllPrices();
    });

    console.log("Tax.js: initialized");
  }

  // --------------------------------------------------------------------------
  // Boot when DOM is ready
  // --------------------------------------------------------------------------
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
